// Warm up <model>? (acceptance G1–G6): pick a GPU this cluster offers — the ones
// that can't hold the model are disabled with the reason — see the cost, go.
import { useEffect, useState } from "react";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { gpuName, money, needsText, optionReason, presetLabel, shortInstance } from "./modelServingModel";
import { Btn, Dialog, Field, Notice, errorText } from "./modelServingUi";

export default function WarmUpDialog({ model, onClose, onDone }) {
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState(model.node_class);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    modelServingApi.gpuOptions(model.key)
      .then((r) => { if (live) setOpts(r.data || []); })
      .catch((e) => { if (live) { setOpts([]); setErr(errorText(e)); } });
    return () => { live = false; };
  }, [model.key]);

  const chosen = (opts || []).find((o) => o.key === pick);
  const need = needsText(model, { vram_required_gb: (opts || [])[0]?.vram_required_gb, quantization: model.quantization });

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await modelServingApi.warm(model.key, pick !== model.node_class ? pick : undefined);
      onDone?.();
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };

  return (
    <Dialog testid="warm-dialog" title={`Warm up ${model.display_name}?`} glyph="▶" maxWidth={480} onClose={onClose}
      sub="Starts a GPU node now and loads the model from your S3 library, so the first request doesn't wait."
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy || !chosen?.fits} onClick={go}>{busy ? "Starting…" : "Warm up"}</Btn>
        </>
      )}>
      <Field label="GPU" hint={need || undefined}>
        <div data-lc="gpu-choice-warm" role="radiogroup" aria-label="GPU" style={{ display: "grid", gap: 8 }}>
          {opts === null && <div style={{ fontSize: 12.5, color: COLORS.textDim }}>Loading the GPUs this cluster offers…</div>}
          {(opts || []).map((o) => {
            const on = o.key === pick;
            return (
              <label key={o.key} data-testid={`gpu-${o.key}`}
                style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", borderRadius: 12,
                  border: `1px solid ${on ? COLORS.accent : COLORS.border}`, opacity: o.fits ? 1 : 0.55,
                  background: on ? COLORS.accentSoft : "transparent", cursor: o.fits ? "pointer" : "not-allowed" }}>
                <input type="radio" name="ms-warm-gpu" value={o.key} checked={on} disabled={!o.fits}
                  onChange={() => setPick(o.key)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {gpuName(o)}
                    {o.key === model.node_class && (
                      <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: 0.6, color: COLORS.accentText }}>CURRENT</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: COLORS.textDim }}>
                    {shortInstance(o.node)} · {presetLabel(o).split(" · ").pop()}
                    {!o.fits && ` · ${optionReason(o, model.quantization)}`}
                  </div>
                </div>
                <div style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>{money(o.price_per_hour)}/hr</div>
              </label>
            );
          })}
        </div>
      </Field>
      <Field label="Cost while running"
        hint={`AWS on-demand price (approx.). Ready in ~2–5 min. Parks itself (back to $0) after ${model.idle_timeout_minutes || 15} min with no requests, or press Stop anytime. Changing the GPU is saved as this model's default.`}>
        <div data-lc="warm-cost" style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
          {money(chosen?.price_per_hour)}<small style={{ fontSize: 12, color: COLORS.textDim }}>/hr</small>
        </div>
      </Field>
      {err && <Notice testid="warm-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}
